import { useMsal } from '@azure/msal-react';
import {
    Button,
    Checkbox,
    Input,
    makeStyles,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    tokens
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Dismiss24Regular, Edit24Regular, Save24Regular } from '@fluentui/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import RequiredFieldFrame from '../../components/RequiredFieldFrame';
import { useProject } from '../../context/ProjectContext';
import type { ProjectUserInfo } from '../../models/ProjectUserInfo';
import type { ProjektItem } from '../../models/ProjektItem';
import { DataService } from '../../services/dataService';
import useConfigStore from '../../store/config';
import { getVisibleMissingEckdatenFields, isRequiredFieldMissing } from '../../utils/eckdatenCompleteness';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalL,
    },
    headerTitle: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorNeutralForeground1,
    },
    categorySection: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalL,
        marginBottom: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalL,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    categoryHeader: {
        fontSize: tokens.fontSizeBase500,
        fontWeight: 700,
        color: tokens.colorBrandForeground1,
        paddingTop: '2px',
    },
    fieldsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    fieldBox: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gap: tokens.spacingHorizontalM,
        alignItems: 'start',
        paddingBottom: 0,
    },
    fieldLabel: {
        fontWeight: 600,
        fontSize: '11px',
        color: tokens.colorNeutralForeground2,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        paddingTop: '4px',
    },
    fieldValue: {
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        wordWrap: 'break-word',
    },
    emptyValue: {
        color: tokens.colorNeutralForeground3,
        fontStyle: 'italic',
    },
    booleanValue: {
        padding: '2px 6px',
        borderRadius: '2px',
        fontSize: '11px',
        fontWeight: 600,
        display: 'inline-block',
        width: 'fit-content',
    },
    booleanTrue: {
        backgroundColor: tokens.colorStatusSuccessBackground2,
        color: tokens.colorStatusSuccessForeground1,
    },
    booleanFalse: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
    inputField: {
        width: '100%',
        fontSize: tokens.fontSizeBase300,
        height: '32px',
    },
});

interface EckdatenInternalOversightProps {
    project: ProjektItem | null;
}

interface FieldDefinition {
    label: string;
    key: keyof ProjektItem | string;
    isBoolean?: boolean;
    isDate?: boolean;
    editable?: boolean;
}

interface DataGroup {
    title: string;
    fields: FieldDefinition[];
}

interface EditedValues {
    [key: string]: string | number | boolean | undefined;
}

interface PermissionConfig {
    'Rolle Administratoren'?: 'r' | 'rw';
    'Rolle Hochschulleitung'?: 'r' | 'rw';
    'Rolle Controlling'?: 'r' | 'rw';
    'Rolle Accounting'?: 'r' | 'rw';
    'Rolle Vertragskoordination'?: 'r' | 'rw';
    'Rolle Forschungsservice'?: 'r' | 'rw';
    'Rolle Rektorat'?: 'r' | 'rw';
    isBenutzerProjektLeiter?: 'r' | 'rw';
    isBenutzerProjektleiterAssistent?: 'r' | 'rw';
    departmentHead?: 'r' | 'rw';
    isRegularUser?: 'r' | 'rw';
}

const formatDateForDisplay = (date: unknown): string => {
    if (!date) return '-';
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date instanceof Date ? date : null;
        if (!dateObj) return String(date);
        const year = dateObj.getFullYear();
        // Return '-' for placeholder dates (year 1 or very old dates like 0001-01-01)
        if (year <= 1) return '-';
        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        return `${day}.${month}.${year}`;
    } catch {
        return String(date);
    }
};

const formatDateForInput = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        const year = dateObj.getFullYear();

        // Treat default/placeholder dates (before year 2000) as empty
        if (year < 2000) return '';

        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const canEditField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager
    if (project.isBenutzerProjektLeiter && fieldPermissions.isBenutzerProjektLeiter === 'rw') {
        return true;
    }

    // Check if user is a project manager assistant
    if (project.isBenutzerProjektleiterAssistent && fieldPermissions.isBenutzerProjektleiterAssistent === 'rw') {
        return true;
    }

    // Check if user is department head (if departmentLeiterID matches current user)
    if (userInfo && project.departmentLeiterID === userInfo.userId && fieldPermissions.departmentHead === 'rw') {
        return true;
    }

    // Check role-based permissions
    if (userRole === 'Administratoren' && fieldPermissions['Rolle Administratoren'] === 'rw') {
        return true;
    }

    if (userRole === 'Hochschulleitung' && fieldPermissions['Rolle Hochschulleitung'] === 'rw') {
        return true;
    }

    if (userRole === 'Controlling' && fieldPermissions['Rolle Controlling'] === 'rw') {
        return true;
    }

    if (userRole === 'Accounting' && fieldPermissions['Rolle Accounting'] === 'rw') {
        return true;
    }

    if (userRole === 'Vertragskoordination' && fieldPermissions['Rolle Vertragskoordination'] === 'rw') {
        return true;
    }

    if (userRole === 'Forschungsservice' && fieldPermissions['Rolle Forschungsservice'] === 'rw') {
        return true;
    }

    if (userRole === 'Rektorat' && fieldPermissions['Rolle Rektorat'] === 'rw') {
        return true;
    }

    // Check if user is a regular user (fallback for users without special roles)
    if (fieldPermissions.isRegularUser === 'rw') {
        return true;
    }

    return false;
};

const canViewField = (
    fieldPermissions: PermissionConfig | undefined,
    userRole: string | null,
    project: ProjektItem | null,
    userInfo: ProjectUserInfo | null
): boolean => {
    if (!fieldPermissions || !project) return false;

    // Check if user is a project manager (read or write)
    if (project.isBenutzerProjektLeiter &&
        (fieldPermissions.isBenutzerProjektLeiter === 'rw' || fieldPermissions.isBenutzerProjektLeiter === 'r')) {
        return true;
    }

    // Check if user is a project manager assistant (read or write)
    if (project.isBenutzerProjektleiterAssistent &&
        (fieldPermissions.isBenutzerProjektleiterAssistent === 'rw' || fieldPermissions.isBenutzerProjektleiterAssistent === 'r')) {
        return true;
    }

    // Check if user is department head (if departmentLeiterID matches current user)
    if (userInfo && project.departmentLeiterID === userInfo.userId &&
        (fieldPermissions.departmentHead === 'rw' || fieldPermissions.departmentHead === 'r')) {
        return true;
    }

    // Check role-based permissions (read or write)
    if (userRole === 'Administratoren' && (fieldPermissions['Rolle Administratoren'] === 'rw' || fieldPermissions['Rolle Administratoren'] === 'r')) {
        return true;
    }

    if (userRole === 'Hochschulleitung' && (fieldPermissions['Rolle Hochschulleitung'] === 'rw' || fieldPermissions['Rolle Hochschulleitung'] === 'r')) {
        return true;
    }

    if (userRole === 'Controlling' && (fieldPermissions['Rolle Controlling'] === 'rw' || fieldPermissions['Rolle Controlling'] === 'r')) {
        return true;
    }

    if (userRole === 'Accounting' && (fieldPermissions['Rolle Accounting'] === 'rw' || fieldPermissions['Rolle Accounting'] === 'r')) {
        return true;
    }

    if (userRole === 'Vertragskoordination' && (fieldPermissions['Rolle Vertragskoordination'] === 'rw' || fieldPermissions['Rolle Vertragskoordination'] === 'r')) {
        return true;
    }

    if (userRole === 'Forschungsservice' && (fieldPermissions['Rolle Forschungsservice'] === 'rw' || fieldPermissions['Rolle Forschungsservice'] === 'r')) {
        return true;
    }

    if (userRole === 'Rektorat' && (fieldPermissions['Rolle Rektorat'] === 'rw' || fieldPermissions['Rolle Rektorat'] === 'r')) {
        return true;
    }

    // Check if user is a regular user (fallback for users without special roles)
    if (fieldPermissions.isRegularUser === 'r' || fieldPermissions.isRegularUser === 'rw') {
        return true;
    }

    return false;
};

const initializeEditedValues = (project: ProjektItem | null): EditedValues => {
    if (!project) return {};
    return {
        mitarbeitForschungsserviceErforderlich: project.mitarbeitForschungsserviceErforderlich || false,
        personalProjektStundenReduktion: project.personalProjektStundenReduktion || 0,
        personalNeuanstellungImProjekt: project.personalNeuanstellungImProjekt || 0,
        vollzeitAequivalentePlan: String(project.vollzeitAequivalentePlan || ''),
        vollzeitAequivalenteIst: String(project.vollzeitAequivalenteIst || ''),
        ausgeblendet: project.ausgeblendetAsText || undefined,
        dokumenteAusgeblendet: project.dokumenteAusgeblendet || undefined,
        storniertArchiviert: project.storniertArchiviert || false,
        archived: formatDateForInput(project.archived),
        datumFreigabeProjektidee: formatDateForInput(project.datumFreigabeProjektidee),
        datumFreigabeAngebot: formatDateForInput(project.datumFreigabeAngebot),
        datumFreigabeProjekt: formatDateForInput(project.datumFreigabeProjekt),
        freigabeFBL: project.freigabeFBL || false,
        freigabeGF: project.freigabeGF || false,
        datumFreigabeGF: formatDateForInput(project.datumFreigabeGF),
        zugeschlagen: project.zugeschlagen || false,
        checkAntragAngebotQualitaet: project.checkAntragAngebotQualitaet || undefined,
        checkAntragAngebotAdaequanz: project.checkAntragAngebotAdaequanz || undefined,
        checkAntragAngebotForm: project.checkAntragAngebotForm || undefined,
        erfolgreichAbgewickelt: project.erfolgreichAbgewickelt || false,
        risikoueberpruefungNotwendig: project.risikoueberpruefungNotwendig || false,
    };
};

const EckdatenInternalOversight: React.FC<EckdatenInternalOversightProps> = ({ project: _project }) => {
    const styles = useStyles();
    const { selectedRole } = useConfigStore((state) => state.roleConfig);
    const { userInfo: configUserInfo } = useConfigStore((state) => state.userConfig);
    const { accounts, instance } = useMsal();
    const { refreshProject, userInfo } = useProject();
    const [isEditing, setIsEditing] = useState(false);
    const [editedValues, setEditedValues] = useState<EditedValues>(() => initializeEditedValues(_project));
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const missingFields = useMemo(() => getVisibleMissingEckdatenFields(_project, userInfo), [_project, userInfo]);

    // DataService instance
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Centralized field permissions
    const allFieldPermissions = useMemo(() => {
        const permissions: Array<{ key: string; permissions: PermissionConfig }> = [
            // Project Data Section
            { key: 'mitarbeitForschungsserviceErforderlich', permissions: { 'Rolle Administratoren': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            // Personal Section
            { key: 'personalProjektStundenReduktion', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'personalNeuanstellungImProjekt', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'vollzeitAequivalentePlan', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'vollzeitAequivalenteIst', permissions: { 'Rolle Administratoren': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r' } },
            // Visibility Section
            { key: 'ausgeblendet', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'dokumenteAusgeblendet', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            // Project Approvals Section
            { key: 'storniertArchiviert', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'rw', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'archived', permissions: { 'Rolle Administratoren': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'datumFreigabeProjektidee', permissions: { 'Rolle Administratoren': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'datumFreigabeAngebot', permissions: { 'Rolle Administratoren': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'datumFreigabeProjekt', permissions: { 'Rolle Administratoren': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'freigabeFBL', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'rw', isRegularUser: 'r' } },
            { key: 'freigabeGF', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'datumFreigabeGF', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'zugeschlagen', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'rw', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'rw', isBenutzerProjektleiterAssistent: 'rw', departmentHead: 'r', isRegularUser: 'r' } },
            // Document Verification Section
            { key: 'checkAntragAngebotQualitaet', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'checkAntragAngebotAdaequanz', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'checkAntragAngebotForm', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            // Additional Information Section
            { key: 'erfolgreichAbgewickelt', permissions: { 'Rolle Administratoren': 'rw', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
            { key: 'risikoueberpruefungNotwendig', permissions: { 'Rolle Administratoren': 'r', 'Rolle Hochschulleitung': 'r', 'Rolle Controlling': 'r', 'Rolle Accounting': 'r', 'Rolle Vertragskoordination': 'r', 'Rolle Forschungsservice': 'r', 'Rolle Rektorat': 'r', isBenutzerProjektLeiter: 'r', isBenutzerProjektleiterAssistent: 'r', departmentHead: 'r', isRegularUser: 'r' } },
        ];
        return permissions;
    }, []);

    // Check if user can edit any field
    const canEditAny = useMemo(() => {
        if (!_project || !configUserInfo) return false;
        return allFieldPermissions.some((field) => {
            return canEditField(field.permissions, selectedRole, _project, configUserInfo);
        });
    }, [_project, selectedRole, configUserInfo, allFieldPermissions]);

    useEffect(() => {
        setEditedValues(initializeEditedValues(_project));
    }, [_project]);

    const handleFieldChange = (key: string, value: string | boolean) => {
        setEditedValues((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleSave = async () => {
        if (!_project || !dataService) return;

        setIsSaving(true);
        try {
            // Helper to convert date string (YYYY-MM-DD) back to Date in UTC to avoid timezone offset issues
            const stringToDate = (dateStr: string | undefined): Date | null | undefined => {
                if (!dateStr) return undefined;
                try {
                    const [year, month, day] = dateStr.split('-');
                    if (!year || !month || !day) return undefined;
                    // Use UTC to avoid timezone offset issues when serializing to JSON
                    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
                } catch {
                    return undefined;
                }
            };

            const updatedProject: ProjektItem = {
                ..._project,
                mitarbeitForschungsserviceErforderlich: editedValues.mitarbeitForschungsserviceErforderlich as boolean,
                personalProjektStundenReduktion: editedValues.personalProjektStundenReduktion ? Number(editedValues.personalProjektStundenReduktion) : 0,
                personalNeuanstellungImProjekt: editedValues.personalNeuanstellungImProjekt ? Number(editedValues.personalNeuanstellungImProjekt) : 0,
                ausgeblendet: editedValues.ausgeblendet === 'Yes' ? true : false,
                dokumenteAusgeblendet: editedValues.dokumenteAusgeblendet === 'Yes' ? true : false,
                storniertArchiviert: editedValues.storniertArchiviert as boolean,
                archived: stringToDate(editedValues.archived as string) ?? _project.archived,
                datumFreigabeProjektidee: stringToDate(editedValues.datumFreigabeProjektidee as string) ?? _project.datumFreigabeProjektidee,
                datumFreigabeAngebot: stringToDate(editedValues.datumFreigabeAngebot as string) ?? _project.datumFreigabeAngebot,
                datumFreigabeProjekt: stringToDate(editedValues.datumFreigabeProjekt as string) ?? _project.datumFreigabeProjekt,
                freigabeFBL: editedValues.freigabeFBL as boolean,
                freigabeGF: editedValues.freigabeGF as boolean,
                datumFreigabeGF: stringToDate(editedValues.datumFreigabeGF as string) ?? _project.datumFreigabeGF,
                zugeschlagen: editedValues.zugeschlagen as boolean,
                checkAntragAngebotQualitaet: editedValues.checkAntragAngebotQualitaet as boolean,
                checkAntragAngebotAdaequanz: editedValues.checkAntragAngebotAdaequanz as boolean,
                checkAntragAngebotForm: editedValues.checkAntragAngebotForm as boolean,
                erfolgreichAbgewickelt: editedValues.erfolgreichAbgewickelt as boolean,
                risikoueberpruefungNotwendig: editedValues.risikoueberpruefungNotwendig as boolean,
            };

            const result = await dataService.updateProjekt(updatedProject);

            if (result.success) {
                if (refreshProject) {
                    await refreshProject();
                }
                setShowSuccessMessage(true);
                setErrorMessage(null);
                setTimeout(() => setShowSuccessMessage(false), 3000);
                setIsEditing(false);
            } else {
                setShowSuccessMessage(false);
                setErrorMessage(result.message);
                setTimeout(() => setErrorMessage(null), 5000);
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            setShowSuccessMessage(false);
            setErrorMessage('Error saving. Please try again.');
            setTimeout(() => setErrorMessage(null), 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (isSaving) return;
        setEditedValues(initializeEditedValues(_project));
        setIsEditing(false);
    };

    // Define fields for Internal Oversight tab based on CSV mapping
    const dataGroups: DataGroup[] = [
        {
            title: 'Project Data',
            fields: [
                { label: 'Research Service Required', key: 'mitarbeitForschungsserviceErforderlich', isBoolean: true, editable: false },
            ],
        },
        {
            title: 'Personal',
            fields: [
                { label: 'Hour Reduction in Project', key: 'personalProjektStundenReduktion', isBoolean: true, editable: true },
                { label: 'New Hire in Project', key: 'personalNeuanstellungImProjekt', isBoolean: true, editable: true },
                { label: 'Full-Time Equivalents (Target)', key: 'vollzeitAequivalentePlan', editable: true },
                { label: 'Full-Time Equivalents (Actual)', key: 'vollzeitAequivalenteIst', editable: true },
            ],
        },
        {
            title: 'Visibility',
            fields: [
                { label: 'Visible only to superordinate roles and project team', key: 'ausgeblendet', isBoolean: true, editable: true },
                { label: 'Project documents visible only to superordinate roles and project managers', key: 'dokumenteAusgeblendet', isBoolean: true, editable: true },
            ],
        },
        {
            title: 'Project Approvals',
            fields: [
                { label: 'Archived/Cancelled', key: 'storniertArchiviert', isBoolean: true, editable: true },
                { label: 'Archive/Cancellation Date', key: 'archived', isDate: true, editable: true },
                { label: 'Release Date Project Idea', key: 'datumFreigabeProjektidee', isDate: true, editable: false },
                { label: 'Release Date Funding Application/Offer', key: 'datumFreigabeAngebot', isDate: true, editable: false },
                { label: 'Release Date Funding Approval/Order Confirmation', key: 'datumFreigabeProjekt', isDate: true, editable: false },
                { label: 'Release by Department Head/Director', key: 'freigabeFBL', isBoolean: true, editable: true },
                { label: 'Release by General Manager', key: 'freigabeGF', isBoolean: true, editable: true },
                { label: 'Release Date General Manager', key: 'datumFreigabeGF', isDate: true, editable: true },
                { label: 'Awarded', key: 'zugeschlagen', isBoolean: true, editable: true },
            ],
        },
        {
            title: 'Document Verification',
            fields: [
                { label: 'Document meets quality criteria', key: 'checkAntragAngebotQualitaet', isBoolean: true, editable: true },
                { label: 'Document meets adequacy criteria', key: 'checkAntragAngebotAdaequanz', isBoolean: true, editable: true },
                { label: 'Document meets format criteria', key: 'checkAntragAngebotForm', isBoolean: true, editable: true },
            ],
        },
        {
            title: 'Additional Information',
            fields: [
                { label: 'Project successfully handled', key: 'erfolgreichAbgewickelt', isBoolean: true, editable: true },
                { label: 'Risk review necessary', key: 'risikoueberpruefungNotwendig', isBoolean: true, editable: false },
            ],
        },
    ];

    const renderDisplayField = (field: FieldDefinition): React.ReactNode => {
        let displayValue = '';

        if (!_project) {
            displayValue = '-';
        } else {
            const key = field.key as keyof ProjektItem;
            const value = _project[key];

            if (field.isBoolean) {
                const isTrue = value === true || value === 'Ja' || value === 'Yes' || value === 'true' || value === 1;
                return (
                    <div className={styles.fieldBox}>
                        <div className={styles.fieldLabel}>{field.label}</div>
                        <div className={`${styles.booleanValue} ${isTrue ? styles.booleanTrue : styles.booleanFalse}`}>
                            {isTrue ? 'Yes' : 'No'}
                        </div>
                    </div>
                );
            }

            if (field.isDate) {
                displayValue = formatDateForDisplay(value);
            } else {
                displayValue = value ? String(value) : '-';
            }
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{field.label}</div>
                <div className={styles.fieldValue}>
                    {displayValue === '-' ? <span className={styles.emptyValue}>-</span> : displayValue}
                </div>
            </div>
        );
    };

    const renderEditField = (field: FieldDefinition): React.ReactNode => {
        const value = editedValues[field.key];

        if (field.isBoolean) {
            const isTrue = value === 'Ja' || value === 'Yes' || value === 'true' || value === 1 || value === true;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <div style={{ marginTop: '2px' }}>
                        <Checkbox
                            checked={isTrue}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onChange={(_ev: any, data: any) => {
                                handleFieldChange(field.key as string, data.checked === true);
                            }}
                        />
                    </div>
                </div>
            );
        }

        if (field.isDate) {
            const dateValue = value ? new Date(String(value)) : undefined;
            return (
                <div className={styles.fieldBox}>
                    <div className={styles.fieldLabel}>{field.label}</div>
                    <DatePicker
                        value={dateValue}
                        onSelectDate={(date) => {
                            if (date) {
                                const formatted = formatDateForInput(date);
                                handleFieldChange(field.key as string, formatted);
                            } else {
                                handleFieldChange(field.key as string, '');
                            }
                        }}
                        placeholder="Select date"
                        formatDate={(date) => {
                            if (!date) return '';
                            const day = String(date.getDate()).padStart(2, '0');
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const year = date.getFullYear();
                            return `${day}.${month}.${year}`;
                        }}
                        parseDateFromString={(dateString) => {
                            const parts = dateString.split('.');
                            if (parts.length === 3) {
                                const date = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                                return date;
                            }
                            return null;
                        }}
                    />
                </div>
            );
        }

        return (
            <div className={styles.fieldBox}>
                <div className={styles.fieldLabel}>{field.label}</div>
                <Input
                    value={String(value || '')}
                    onChange={(_ev: unknown, data: { value?: string }) => {
                        handleFieldChange(field.key as string, data.value || '');
                    }}
                    className={styles.inputField}
                />
            </div>
        );
    };

    const renderField = (field: FieldDefinition) => {
        // Look up field permissions from centralized array
        const fieldPermConfig = allFieldPermissions.find((f) => f.key === field.key);
        const fieldPermissions = fieldPermConfig?.permissions;
        const isFieldEditable = fieldPermissions ? canEditField(fieldPermissions, selectedRole, _project, configUserInfo) : false;
        const isFieldVisible = fieldPermissions ? canViewField(fieldPermissions, selectedRole, _project, configUserInfo) : true;

        if (!isFieldVisible) return null;
        // Required-but-unset fields are framed in both edit and display mode.
        const content = isFieldEditable && isEditing && field.editable !== false
            ? renderEditField(field)
            : renderDisplayField(field);

        return (
            <RequiredFieldFrame isMissing={isRequiredFieldMissing(missingFields, field.key)}>
                {content}
            </RequiredFieldFrame>
        );
    };

    if (!_project) return null;

    return (
        <div className={styles.container}>
            {isSaving && (
                <MessageBar>
                    <MessageBarBody>
                        <MessageBarTitle>Saving...</MessageBarTitle>
                        Please wait while your changes are being saved.
                    </MessageBarBody>
                </MessageBar>
            )}
            {showSuccessMessage && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>Success</MessageBarTitle>
                        Internal Oversight data saved successfully!
                    </MessageBarBody>
                </MessageBar>
            )}
            {errorMessage && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Error</MessageBarTitle>
                        {errorMessage}
                    </MessageBarBody>
                </MessageBar>
            )}
            {/* Header with Edit Button */}
            {canEditAny && (
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Internal Oversight</div>
                    <div>
                        {isEditing ? (
                            <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                <Button
                                    appearance="primary"
                                    icon={<Save24Regular />}
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    appearance="secondary"
                                    icon={<Dismiss24Regular />}
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        ) : (
                            <Button
                                appearance="outline"
                                icon={<Edit24Regular />}
                                onClick={() => setIsEditing(true)}
                            >
                                Edit
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {dataGroups.map((group) => (
                <div key={group.title} className={styles.categorySection}>
                    <div className={styles.categoryHeader}>{group.title}</div>
                    <div className={styles.fieldsContainer}>
                        {group.fields.map((field) => (
                            <div key={field.label}>
                                {renderField(field)}
                            </div>
                        ))}
                    </div>
                </div>
            ))}

            {/* Footer Save/Cancel Buttons */}
            {isEditing && canEditAny && (
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalL, borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: tokens.spacingVerticalL }}>
                    <Button
                        appearance="primary"
                        icon={<Save24Regular />}
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        Save
                    </Button>
                    <Button
                        appearance="secondary"
                        icon={<Dismiss24Regular />}
                        onClick={handleCancel}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
};

export default EckdatenInternalOversight;
